import { isUUID, type UUID, uuidv4 } from "../../../utils/uuid";
import type { EmailMoment } from "../../email-moment/types";
import type { SupabasePollService } from "../../poll/server/supabase-poll-service";
import type { UnlayerDesign } from "../types/unlayer-design";
import { getUnlayerPollBlocks } from "../utils/unlayer-poll-blocks";

type PollService = Pick<SupabasePollService, "upsertPollWithOptionIds">;

type PersistUnlayerPollsParams = {
  accountId: UUID;
  emailMoment: EmailMoment;
  pollService: PollService;
};

type PersistUnlayerPollsResult = {
  emailMoment: EmailMoment;
  pollIds: UUID[];
};

type UnlayerPollOptionValue = {
  id: string;
  text: string;
};

function cloneDesign(design: UnlayerDesign): UnlayerDesign {
  return JSON.parse(JSON.stringify(design)) as UnlayerDesign;
}

function isPollOption(value: unknown): value is UnlayerPollOptionValue {
  return (
    !!value &&
    typeof value === "object" &&
    typeof (value as { id?: unknown }).id === "string" &&
    typeof (value as { text?: unknown }).text === "string"
  );
}

function parsePollOptions(value: string | undefined): { id: UUID; text: string }[] {
  if (!value) return [];

  try {
    const options = JSON.parse(value) as unknown;
    if (!Array.isArray(options) || !options.every(isPollOption)) return [];

    return options
      .map((option) => ({ id: option.id, text: option.text.trim() }))
      .filter((option): option is { id: UUID; text: string } => {
        return isUUID(option.id) && option.text.length > 0;
      });
  } catch {
    return [];
  }
}

export async function persistUnlayerPolls({
  accountId,
  emailMoment,
  pollService,
}: PersistUnlayerPollsParams): Promise<PersistUnlayerPollsResult> {
  if (!emailMoment.design) return { emailMoment, pollIds: [] };

  const design = cloneDesign(emailMoment.design);
  const pollIds: UUID[] = [];

  for (const block of getUnlayerPollBlocks(design)) {
    const values = block.values;
    if (!values) continue;

    const question = values.question?.trim();
    const options = parsePollOptions(values.pollOptions);

    if (!question || options.length === 0) continue;

    const pollId = isUUID(values.pollId) ? values.pollId : uuidv4();
    block.values = { ...values, pollId };
    pollIds.push(pollId);

    await pollService.upsertPollWithOptionIds({
      accountId,
      pollId,
      question,
      options: options.map((option, index) => ({
        ...option,
        ordering: index + 1,
      })),
    });
  }

  return {
    emailMoment: {
      ...emailMoment,
      design,
    },
    pollIds,
  };
}
